import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardCheck, Loader2, ListChecks } from 'lucide-react';
import { fetchStartingEvaluation, submitStartingEvaluation } from '../api/candidate';
import type { StartingEvaluation, StartingEvalQuestion, StartingEvalSection } from '../types';
import { Badge, Button, Card, EmptyState, ProgressBar, cx } from '../components/charte';

// Évaluation de départ (refonte du 14/10/2026) : mêmes questions, même envoi. Choix en pilules, compteur
// des réponses obligatoires, questions manquantes signalées, bouton d'envoi collé en bas sur téléphone.

type Answers = Record<string, string | string[]>;

function Choice({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cx(
        'min-h-[40px] rounded-pill border px-4 py-2 text-left text-sm font-medium transition',
        active ? 'border-primary bg-primary text-white shadow-btn' : 'border-line bg-white text-body hover:border-primary-200',
      )}
    >
      {label}
    </button>
  );
}

/** Une question à choix unique / quiz. */
function SingleChoice({ q, value, onChange }: { q: StartingEvalQuestion; value: string | undefined; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {q.options?.map((opt) => (
        <Choice key={opt.value} active={value === opt.value} label={opt.label} onClick={() => onChange(opt.value)} />
      ))}
    </div>
  );
}

function MultiChoice({ q, value, onChange }: { q: StartingEvalQuestion; value: string[] | undefined; onChange: (v: string[]) => void }) {
  const selected = value ?? [];
  const toggle = (v: string) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className="flex flex-wrap gap-2">
      {q.options?.map((opt) => (
        <Choice key={opt.value} active={selected.includes(opt.value)} label={opt.label} onClick={() => toggle(opt.value)} />
      ))}
    </div>
  );
}

function QuestionField({
  q,
  answers,
  setAnswer,
  missing,
}: {
  q: StartingEvalQuestion;
  answers: Answers;
  setAnswer: (code: string, v: string | string[]) => void;
  missing: boolean;
}) {
  const raw = answers[q.code];
  return (
    <div className={cx('border-t border-slate-100 py-4 first:border-t-0 first:pt-0', missing && 'rounded-row bg-danger-light/60 px-3 first:pt-3')}>
      <p className="mb-2.5 text-sm font-medium text-heading">
        {q.label}
        {!q.optional && <span className="text-primary"> *</span>}
      </p>
      {q.type === 'multi' ? (
        <MultiChoice q={q} value={Array.isArray(raw) ? raw : []} onChange={(v) => setAnswer(q.code, v)} />
      ) : q.type === 'text' ? (
        <input
          type="text"
          aria-label={q.label}
          value={typeof raw === 'string' ? raw : ''}
          onChange={(e) => setAnswer(q.code, e.target.value)}
          className="h-11 w-full rounded-xl border border-line bg-surface-light px-3.5 text-sm text-body outline-none focus:border-primary-300 focus:bg-white focus:ring-4 focus:ring-primary-50"
          placeholder="Votre réponse…"
        />
      ) : (
        <SingleChoice q={q} value={typeof raw === 'string' ? raw : undefined} onChange={(v) => setAnswer(q.code, v)} />
      )}
      {q.hint && <p className="mt-2 text-xs text-muted">{q.hint}</p>}
      {missing && <p className="mt-2 text-xs font-medium text-danger-dark">Réponse attendue.</p>}
    </div>
  );
}

const isEmpty = (v: string | string[] | undefined) =>
  v == null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);

/** Liste des questions obligatoires non répondues. */
function missingRequired(sections: StartingEvalSection[], answers: Answers): string[] {
  const missing: string[] = [];
  for (const s of sections) {
    for (const q of s.questions) {
      if (!q.optional && isEmpty(answers[q.code])) missing.push(q.code);
    }
  }
  return missing;
}

function CompletedSummary({ data }: { data: StartingEvaluation }) {
  return (
    <Card className="text-center">
      <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-success-light text-success">
        <CheckCircle2 size={26} aria-hidden />
      </span>
      <h2 className="text-title-sm text-heading">Évaluation de départ réalisée</h2>
      {data.estimatedHours != null && (
        <p className="mx-auto mt-4 max-w-md rounded-row bg-surface-indigo px-4 py-3 text-sm text-primary-700">
          <strong>Estimation initiale : {data.estimatedHours} h de conduite</strong>
          <br />
          À confirmer avec votre moniteur après l’évaluation pratique.
        </p>
      )}
      <p className="mt-4 text-sm text-secondary">
        Merci ! Votre auto-école a bien reçu vos réponses. Elles serviront de base pour personnaliser votre formation.
      </p>
      {data.knowledgeMax != null && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-pill bg-surface-indigo px-4 py-2">
          <ListChecks size={16} className="text-primary" aria-hidden />
          <span className="text-sm text-body">
            Connaissance du véhicule :{' '}
            <b className="text-primary-700">
              {data.knowledgeScore}/{data.knowledgeMax}
            </b>
          </span>
        </div>
      )}
    </Card>
  );
}

function Title({ children }: { children?: React.ReactNode }) {
  return (
    <div>
      <h1 className="text-title-md text-heading sm:text-title-lg">Évaluation de départ</h1>
      {children}
    </div>
  );
}

export default function EvaluationView() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate', 'starting-evaluation'],
    queryFn: fetchStartingEvaluation,
    staleTime: 60 * 1000,
  });

  const [answers, setAnswers] = useState<Answers>({});
  const [showErrors, setShowErrors] = useState(false);
  const setAnswer = (code: string, v: string | string[]) => setAnswers((prev) => ({ ...prev, [code]: v }));

  const mutation = useMutation({
    mutationFn: () => submitStartingEvaluation(answers),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['candidate', 'starting-evaluation'], fresh);
    },
  });

  const missing = useMemo(() => (data ? missingRequired(data.definition.sections, answers) : []), [data, answers]);
  const required = useMemo(
    () => (data ? data.definition.sections.flatMap((s) => s.questions).filter((q) => !q.optional).length : 0),
    [data],
  );

  if (isLoading) {
    return (
      <Card className="flex items-center text-sm text-secondary">
        <Loader2 className="mr-2 animate-spin" size={16} /> Chargement…
      </Card>
    );
  }

  if (isError || !data || data.status === 'not_requested') {
    return (
      <div className="grid gap-5 sm:gap-6">
        <Title />
        <Card>
          <EmptyState icon={ClipboardCheck} title="Rien à remplir" description="Aucune évaluation de départ ne vous est demandée pour le moment." />
        </Card>
      </div>
    );
  }

  if (data.status === 'completed') {
    return (
      <div className="grid gap-5 sm:gap-6">
        <Title />
        <CompletedSummary data={data} />
      </div>
    );
  }

  const done = required - missing.length;

  // status === 'pending' → formulaire
  return (
    <div className="grid gap-5 sm:gap-6">
      <Title>
        <p className="mt-1 text-sm text-secondary">
          Ce court bilan aide votre auto-école à personnaliser votre formation. Répondez le plus sincèrement possible — il
          n'y a pas d'enjeu de réussite.
        </p>
      </Title>

      {data.definition.sections.map((section) => (
        <Card key={section.code}>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-title-sm text-heading">{section.title}</h2>
            {section.scored && <Badge tone="todo">{data.definition.knowledgeMax} questions</Badge>}
          </div>
          {section.description && <p className="mt-1 text-sm text-secondary">{section.description}</p>}
          <div className="mt-4">
            {section.questions.map((q) => (
              <QuestionField key={q.code} q={q} answers={answers} setAnswer={setAnswer} missing={showErrors && missing.includes(q.code)} />
            ))}
          </div>
        </Card>
      ))}

      {mutation.isError && (
        <p role="alert" className="rounded-row bg-danger-light px-4 py-3 text-sm text-danger-dark">
          Une erreur est survenue lors de l'envoi. Merci de réessayer.
        </p>
      )}

      {/* Envoi : collé en bas de l'écran sur téléphone (au-dessus de la barre de navigation). */}
      <div className="bottom-above-nav sticky z-30">
        <Card className="flex flex-col gap-3 !p-4 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-body">
              {done}/{required} réponse{required > 1 ? 's' : ''} obligatoire{required > 1 ? 's' : ''}
            </p>
            <div className="mt-1.5">
              <ProgressBar value={required ? (done / required) * 100 : 100} label="Réponses obligatoires données" />
            </div>
            {showErrors && missing.length > 0 && (
              <p role="alert" className="mt-1.5 text-xs font-medium text-danger-dark">
                Merci de répondre à toutes les questions obligatoires ({missing.length} restante{missing.length > 1 ? 's' : ''}).
              </p>
            )}
          </div>
          <Button
            variant="primary"
            icon={CheckCircle2}
            loading={mutation.isPending}
            onClick={() => {
              if (missing.length > 0) {
                setShowErrors(true);
                return;
              }
              mutation.mutate();
            }}
          >
            {mutation.isPending ? 'Envoi…' : 'Valider mon évaluation'}
          </Button>
        </Card>
      </div>
    </div>
  );
}
